import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import ChartView from "../../components/ChartView";
import Summary from "../../components/Summary";
import Loading from "../../components/Loading";

import { ITableData, IWrokspaceData } from "../../types/table-type";
import {
  IQueryProps,
  GlobalErrorType,
  PostMessageEventName,
  IPostMessage,
  AppType,
  ChartType,
  IAppConfig,
} from "../../types/global-type";
import { IFormValue } from "../../types/form-type";

import { getAppConfig } from "../../network/index";

import { getFormDefaultValue, parsingAppConfig } from "../../utils/form_main";
import { sortColumnsAndRows } from "../../utils/tableData_main";
import { getAppType } from "../../utils/app";

import { Container } from "./styled";

interface IProps {
  query: IQueryProps;
}

const ViewPage: React.FC<IProps> = ({ query }) => {
  const { tableId, token, blockId, isExpand, sessionId, form, locale } = query;

  // @ts-ignore
  const urlParams = useParams();
  const { appType: appTypeStr } = urlParams as { appType: AppType };
  const appType = getAppType(appTypeStr);

  const [tableData, setTableData] = useState<ITableData | null>();
  const [workspaceData, setWorkspaceData] = useState<IWrokspaceData | null>();
  const [value, setValue] = useState<IFormValue | null>();
  const [loaded, setLoaded] = useState(false);

  const handleViewLond = useCallback(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ eventName: PostMessageEventName.sendReadyStatus, data: { blockId, isExpand } }, "*");
    }
  }, [blockId, isExpand]);

  const loadTableData = useCallback((data?: ITableData) => {
    if (!data) {
      new Error(GlobalErrorType.RESPONSE_ERROR);
    } else {
      sortColumnsAndRows(data);
      setTableData(data);
    }
  }, []);

  const loadWorkspace = useCallback((data?: IWrokspaceData) => {
    if (!data) {
      new Error(GlobalErrorType.RESPONSE_ERROR);
    }
    setWorkspaceData(data);
    const treelab = {
      users: data?.users,
    };
    // @ts-ignore
    window.treelab = treelab;
  }, []);

  const loadAppConfigData = useCallback(
    (data?: IAppConfig) => {
      if (data?.tableId !== tableId && tableData) {
        const defaultValue = getFormDefaultValue(tableData, appType as AppType);
        setValue(defaultValue);
      } else {
        if (appType === AppType.CHART) {
          const newFormValue = {
            ...data?.formValue,
          } as IFormValue<AppType.CHART>;
          if (!newFormValue.chartType) newFormValue.chartType = ChartType.BASIC_BAR;
          setValue(newFormValue);
        } else {
          setValue(data?.formValue);
        }
      }
    },
    [appType, tableData, tableId]
  );

  const fetchAppConfigData = useCallback(() => {
    getAppConfig(blockId, token, sessionId)
      .then((data) => {
        loadAppConfigData(parsingAppConfig(data) || undefined);
      })
      .catch((err) => { })
      .finally(() => { });
  }, [blockId, token, sessionId, loadAppConfigData]);

  useEffect(() => {
    try {
      if (loaded) return;
      if (!token) return;
      if (!tableData) return;
      fetchAppConfigData();
      setLoaded(true);
    } catch (error) {
      console.error(error);
    }
  }, [fetchAppConfigData, loaded, tableData, token]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const { data: eventData, origin } = event;
      const { eventName, data } = eventData as IPostMessage;
      switch (eventName) {
        case PostMessageEventName.closeModalCheck:
          if (window.parent !== window) {
            window.parent.postMessage({ eventName: PostMessageEventName.closeModal }, origin);
          }
          break;
        case PostMessageEventName.refreshAppConfig:
          fetchAppConfigData();
          break;
        case PostMessageEventName.pushTableData:
          loadTableData(data);
          break;
        case PostMessageEventName.pushWorkspaceData:
          loadWorkspace(data);
          break;
      }
    },
    [fetchAppConfigData, loadTableData, loadWorkspace]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage, false);
    window.parent.postMessage(
      {
        eventName: PostMessageEventName.sendReadyForData,
        data: { blockId, isExpand },
      },
      "*"
    );
    return () => {
      window.removeEventListener("message", handleMessage, false);
    };
  }, [blockId, handleMessage, isExpand]);

  if (!workspaceData || !tableData || !value) {
    return <Loading />;
  }

  return (
    <Container>
      {appType === AppType.CHART && (
        <ChartView
          workspaceData={workspaceData}
          tableData={tableData}
          formValue={value as IFormValue<AppType.CHART>}
          from={form}
          onload={handleViewLond}
          isExpand={isExpand}
        />
      )}
      {appType === AppType.AGGREGATE && (
        <Summary
          tableData={tableData}
          formValue={value as IFormValue<AppType.AGGREGATE>}
          onload={handleViewLond}
          isExpand={isExpand}
          locale={locale}
        />
      )}
    </Container>
  );
};

export default ViewPage;
